import React, { createContext, useContext } from "react";
import { useAddAllFontsForFB } from "../../hooks/useFetchAllFonts";

type FontsContextType = ReturnType<typeof useAddAllFontsForFB>;

const FontsContext = createContext<FontsContextType | null>(null);

/**
 * Wraps `useAddAllFontsForFB` so that the Google-Fonts list (~1500 families,
 * chunked into ~15 `<link rel="stylesheet">` tags) is loaded ONCE per session
 * regardless of how many descendants need access to it.
 *
 * Previously the hook was called from both `Tldraw` and `StyleMenu`. When
 * `Tldraw` remounted on certain actions, `hasLoadedRef` reset and the chunked
 * URLs were re-evaluated by the browser, producing a flurry of font CSS
 * requests per user action. Mounting this provider above the components that
 * can remount keeps the `<link>` tags in the document head untouched.
 */
export function FontsProvider({ children }: { children: React.ReactNode }) {
  const fontsData = useAddAllFontsForFB();

  // `useAddAllFontsForFB` returns a fresh object literal on every render
  // and inlines `addCustomFont` / `removeCustomFont` as new arrow functions
  // each time. Passing that object straight to the context provider would
  // re-render every consumer (Tldraw, StyleMenu) on each parent render.
  //
  // Stabilize the callbacks via a ref so we can memoize the context value
  // on the primitive state (`fonts`, `customFontsLoaded`) only. The ref is
  // updated synchronously on every render, so the wrappers always call the
  // latest implementation.
  const fontsDataRef = React.useRef(fontsData);
  fontsDataRef.current = fontsData;

  const addCustomFont = React.useCallback((fontName: string) => fontsDataRef.current.addCustomFont(fontName), []);
  const removeCustomFont = React.useCallback((fontName: string) => fontsDataRef.current.removeCustomFont(fontName), []);

  const value = React.useMemo<FontsContextType>(
    () => ({
      fonts: fontsData.fonts,
      customFontsLoaded: fontsData.customFontsLoaded,
      addCustomFont,
      removeCustomFont,
    }),
    [fontsData.fonts, fontsData.customFontsLoaded, addCustomFont, removeCustomFont]
  );

  return <FontsContext.Provider value={value}>{children}</FontsContext.Provider>;
}

export function useFontsContext(): FontsContextType {
  const context = useContext(FontsContext);
  if (!context) {
    throw new Error("useFontsContext must be used within FontsProvider");
  }
  return context;
}
